import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import EngineRating from "@/components/EngineRating";
import SoundTypeAnalysis from "@/components/SoundTypeAnalysis";
import WisedriveAnalysis from "@/components/WisedriveAnalysis";
import PredictiveChart from "@/components/PredictiveChart";
import ValidationCard from "@/components/ValidationCard";
import ExpertRecommendation from "@/components/ExpertRecommendation";
import AdvancedDiagnostic from "@/components/AdvancedDiagnostic";
import AIDiagnosisSection from "@/components/AIDiagnosisSection";
import BankAnalysis from "@/components/BankAnalysis";
import InsuranceAnalysis from "@/components/InsuranceAnalysis";
import SoundHistogram from "@/components/SoundHistogram";
import MFCWLResults from "@/components/MFCWLResults";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PrintReportPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id || !token) { setError("Missing parameters"); setLoading(false); return; }
    fetch(`${BASE}/api/v1/print-data/${id}?token=${token}`)
      .then(r => { if (!r.ok) throw new Error("Failed to load"); return r.json(); })
      .then(data => setResult(data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, token]);

  if (loading) return <div className="p-8 text-center text-sm text-slate-400">Loading report...</div>;
  if (error || !result) return <div className="p-8 text-center text-red-500">Error: {error}</div>;

  const car = result.car_details || {};
  const isMFCWL = result.source === "mfcwl";

  if (isMFCWL) {
    return <MFCWLPrintLayout result={result} car={car} />;
  }

  return <SoundAIPrintLayout result={result} car={car} />;
}

/* ── MFCWL Print Layout ── */
function MFCWLPrintLayout({ result, car }) {
  return (
    <div className="print-report bg-white min-h-screen" data-testid="print-report-ready">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b-2 border-orange-500">
        <img src="/wisedrive-logo-header.png" alt="Wisedrive" className="h-8" />
        <div className="text-right">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-600">MFCWL Inspection Report</p>
          <p className="text-xs text-slate-400 font-mono">
            {result.created_at ? new Date(result.created_at).toLocaleString() : ""} | ID: {result.id?.slice(0, 8)}
          </p>
        </div>
      </div>

      {/* Vehicle Title */}
      <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
        <h1 className="text-2xl font-bold tracking-tight uppercase font-['Barlow']">
          {car.make} {car.model} ({car.year})
        </h1>
        <p className="text-sm text-slate-500">
          {car.fuel_type} | {car.transmission} | {car.km_driven?.toLocaleString()} km driven
        </p>
      </div>

      {/* MFCWL Results — bars, gauge, analysis, valuation */}
      <div className="px-4 py-4">
        <MFCWLResults result={result} />
      </div>

      {/* Footer */}
      <div className="px-6 mt-4">
        <div className="border-t-2 border-orange-500 pt-4">
          <div className="flex items-center justify-between">
            <img src="/wisedrive-logo-header.png" alt="Wisedrive" className="h-6 opacity-60" />
            <div className="text-right">
              <p className="text-xs text-slate-400">Generated by Wisedrive AI Platform</p>
              <p className="text-xs text-slate-400">&copy; 2026 Wisedrive Technologies | ai.wisedrive.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Sound AI Print Layout (original) ── */
function SoundAIPrintLayout({ result, car }) {
  return (
    <div className="print-report bg-white min-h-screen" data-testid="print-report-ready">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b-2 border-orange-500">
        <img src="/wisedrive-logo-header.png" alt="Wisedrive" className="h-8" />
        <div className="text-right">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Vehicle Sound Analysis Report</p>
          <p className="text-xs text-slate-400 font-mono">{new Date(result.created_at).toLocaleString()}</p>
        </div>
      </div>

      {/* Vehicle Title */}
      <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
        <h1 className="text-2xl font-bold tracking-tight uppercase font-['Barlow']">
          {car.make} {car.model} ({car.year})
        </h1>
        <p className="text-sm text-slate-500">
          {car.fuel_type} | {car.transmission} | {car.km_driven?.toLocaleString()} km driven
        </p>
      </div>

      <div className="px-6 py-5 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EngineRating rating={result.engine_rating} />
          <ValidationCard validation={result.validation} />
        </div>

        <SoundTypeAnalysis data={result.sound_classification} expandAll={true} />
        <WisedriveAnalysis aiAnalysis={result.ai_analysis} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PredictiveChart title="WITH Regular Maintenance" data={result.predictive_data?.maintained} currentKm={result.predictive_data?.current_km} type="maintained" />
          <PredictiveChart title="WITHOUT Maintenance" data={result.predictive_data?.unmaintained} currentKm={result.predictive_data?.current_km} type="unmaintained" />
        </div>

        <ExpertRecommendation predictiveData={result.predictive_data} carDetails={result.car_details} engineRating={result.engine_rating} />
        <BankAnalysis data={result.financial_analysis?.bank_analysis} />
        <InsuranceAnalysis data={result.financial_analysis?.insurance_analysis} />
        <AdvancedDiagnostic spectrogramDiagnostic={result.spectrogram_diagnostic} spectrogramImage={result.diagnostic_spectrogram_image} hasTurbo={result.car_details?.has_turbo || false} />
        <AIDiagnosisSection aiDiagnosis={result.ai_diagnosis} showPartial={true} />
        <SoundHistogram data={result.histogram_data} histogramImage={result.histogram_image} waveformImage={result.waveform_image} />

        {/* Footer */}
        <div className="border-t-2 border-orange-500 pt-4 mt-6">
          <div className="flex items-center justify-between">
            <img src="/wisedrive-logo-header.png" alt="Wisedrive" className="h-6 opacity-60" />
            <div className="text-right">
              <p className="text-xs text-slate-400">Generated by Wisedrive AI Platform</p>
              <p className="text-xs text-slate-400">&copy; 2026 Wisedrive Technologies | ai.wisedrive.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
